/**
 * Global Search Service
 *
 * Service for searching across multiple resource types including:
 * - Plans
 * - Workouts
 * - Exercises
 * - Challenges
 * - Friends
 * - Clients
 *
 * @module services/global-search
 */

import { apiClient } from './api/client';
import { rethrowTyped } from './api/errors';
import {
  groupSearchResults,
  isValidSearchTerm,
  MIN_SEARCH_TERM_LENGTH,
} from '../types/domain/search.types';

import type { QueryParams } from '../types/api';
import type {
  GlobalSearchRequest,
  GlobalSearchResponse,
  GlobalSearchResult,
  GlobalSearchType,
  GroupedSearchResults,
} from '../types/domain/search.types';

// =============================================================================
// GLOBAL SEARCH SERVICE CLASS
// =============================================================================

export class GlobalSearchService {
  private readonly baseUrl = '/search';

  // ---------------------------------------------------------------------------
  // SEARCH OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Perform a global search across all resource types or a specific type.
   *
   * GET /api/search?search=...&type=...
   *
   * @param params - Search parameters including search and optional type filter
   * @returns Array of search results
   * @throws Error if search term is too short or API request fails
   */
  public async search(
    params: GlobalSearchRequest
  ): Promise<GlobalSearchResult[]> {
    const { search, type } = params;

    if (!isValidSearchTerm(search)) {
      throw new Error(
        `Search term must be at least ${MIN_SEARCH_TERM_LENGTH} characters.`
      );
    }

    try {
      const queryParams: QueryParams = { search: search.trim() };

      if (type) {
        queryParams.type = type;
      }

      const response = await apiClient.get<GlobalSearchResponse>(this.baseUrl, {
        params: queryParams,
      });

      return response.data.data;
    } catch (error) {
      console.error('Failed to perform global search:', error);
      rethrowTyped(error, 'Unable to perform search. Please try again.');
    }
  }

  /**
   * Perform a global search and return results grouped by type.
   *
   * @param params - Search parameters including search and optional type filter
   * @returns Grouped search results by type
   * @throws Error if search term is too short or API request fails
   */
  public async searchGrouped(
    params: GlobalSearchRequest
  ): Promise<GroupedSearchResults> {
    const results = await this.search(params);
    return groupSearchResults(results);
  }

  /**
   * Search for a specific resource type.
   *
   * @param search - Search term
   * @param type - Resource type to search
   * @returns Array of search results for the specified type
   * @throws Error if search term is too short or API request fails
   */
  public async searchByType(
    search: string,
    type: GlobalSearchType
  ): Promise<GlobalSearchResult[]> {
    return this.search({ search, type });
  }

  /**
   * Search for plans.
   *
   * @param search - Search term
   * @returns Array of plan search results
   */
  public async searchPlans(search: string): Promise<GlobalSearchResult[]> {
    return this.searchByType(search, 'plan');
  }

  /**
   * Search for workouts.
   *
   * @param search - Search term
   * @returns Array of workout search results
   */
  public async searchWorkouts(search: string): Promise<GlobalSearchResult[]> {
    return this.searchByType(search, 'workout');
  }

  /**
   * Search for exercises.
   *
   * @param search - Search term
   * @returns Array of exercise search results
   */
  public async searchExercises(search: string): Promise<GlobalSearchResult[]> {
    return this.searchByType(search, 'exercises');
  }

  /**
   * Search for challenges.
   *
   * @param search - Search term
   * @returns Array of challenge search results
   */
  public async searchChallenges(search: string): Promise<GlobalSearchResult[]> {
    return this.searchByType(search, 'challenge');
  }

  /**
   * Search for friends.
   *
   * @param search - Search term
   * @returns Array of friend search results
   */
  public async searchFriends(search: string): Promise<GlobalSearchResult[]> {
    return this.searchByType(search, 'friends');
  }

  /**
   * Search for clients (trainer only).
   *
   * @param search - Search term
   * @returns Array of client search results
   */
  public async searchClients(search: string): Promise<GlobalSearchResult[]> {
    return this.searchByType(search, 'clients');
  }
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

/**
 * Default global search service instance.
 */
export const globalSearchService = new GlobalSearchService();
